import { type OutgoingRequestByAssetId } from 'hooks/use-manage-shared-assets'
import { SharedAssetRequestStatus } from 'types/api/sharedAssets/requests'
import { type BaseK8sClusterAsset } from 'types/api/sharedAssets/sharedAssets'

import type { Team } from 'types/api/teams'

export type RequestNode = Pick<
  OutgoingRequestByAssetId,
  'assetId' | 'clusterName' | 'owningTeamId' | 'requestId'
> & {
  name: string
  status: typeof NAMESPACE_STATUS[keyof typeof NAMESPACE_STATUS]
  team: Team
  teamId: string
}

export type TeamNamespace = {
  status: string
  name: string
  teamId: string
  team: Team
  assetId: string
  clusterName: string
  owningTeamId: string
}

export const NAMESPACE_STATUS = {
  requested: SharedAssetRequestStatus.REQUESTED,
  approved: SharedAssetRequestStatus.APPROVED,
  denied: SharedAssetRequestStatus.DENIED,
  revoked: SharedAssetRequestStatus.REVOKED,
  deleted: SharedAssetRequestStatus.DELETED,
  shareable: 'SHAREABLE' // means it belongs to the cluster but the current team is able to request access
} as const

export enum NAMESPACE_CATEGORY {
  AVAILABLE = 'Available',
  PENDING_REQUESTS = 'Pending Requests',
  SHAREABLE_NAMESPACES = 'Shareable Namespaces'
}

export const isRequested = (namespace) =>
  namespace.status === NAMESPACE_STATUS.requested
export const isDenied = (namespace) =>
  namespace.status === NAMESPACE_STATUS.denied
export const isApproved = (namespace) =>
  namespace.status === NAMESPACE_STATUS.approved
export const isRevoked = (namespace) =>
  namespace.status === NAMESPACE_STATUS.revoked
export const isDeleted = (namespace) =>
  namespace.status === NAMESPACE_STATUS.deleted
export const isShareable = (namespace) =>
  namespace.status === NAMESPACE_STATUS.shareable

export function getPendingRequests({
  namespaces = []
}: {
  namespaces: Pick<TeamNamespace, 'name' | 'status'>[] | undefined
}): Pick<TeamNamespace, 'name' | 'status'>[] {
  return namespaces.filter((namespace) => isRequested(namespace))
}

export function getNamespaceDropdownGroup(
  namespace: Pick<TeamNamespace, 'name' | 'status'>
) {
  switch (namespace.status) {
    case NAMESPACE_STATUS.requested:
      return NAMESPACE_CATEGORY.PENDING_REQUESTS
    case NAMESPACE_STATUS.shareable:
      return NAMESPACE_CATEGORY.SHAREABLE_NAMESPACES
    default:
      return NAMESPACE_CATEGORY.AVAILABLE
  }
}

export function getRequestId({ namespace, assetId, teamId }) {
  return `${teamId}#${assetId}#${namespace}`
}

export function createRequests({
  owningTeamId,
  requestingTeamId,
  assetId,
  namespaces
}) {
  if (!namespaces || namespaces.length === 0) return []
  return namespaces.map((namespace) => ({
    owningTeamId,
    teamToShareWith: requestingTeamId,
    assetId,
    namespace
  }))
}

export function getRequestNode({
  rawRequest: {
    currentStatus,
    namespace,
    owningTeamName: _owningTeamName,
    requester: _requester,
    sharedTeam,
    ...rawRequest
  },
  team,
  isShareable
}: {
  isShareable: boolean | null
  rawRequest: OutgoingRequestByAssetId
  team: Team
}): RequestNode {
  return {
    ...rawRequest,
    status: isShareable ? NAMESPACE_STATUS.shareable : currentStatus,
    name: namespace,
    teamId: sharedTeam,
    team
  }
}

export function getClusterByKey({ clusters, key, value }) {
  if (!key) return {}
  return clusters.filter((cluster) => cluster[key] === value)?.[0] ?? {}
}

// BE :  Here if you own the cluster the BE sends the available clusters into
// the availableNamespaces array
// and in currentlySharedNamespaces if you don't
export function getSharedNamespacesFromCluster(
  clusterObject: BaseK8sClusterAsset | undefined,
  currentTeamId: string
) {
  return (
    clusterObject?.[
      currentTeamId === clusterObject?.owningTeamId
        ? 'availableNamespaces'
        : 'currentlySharedNamespaces'
    ] ?? []
  )
}

//////// End of BE

export function getAvailableNamespacesFromCluster(
  clusterObject: BaseK8sClusterAsset | undefined,
  currentTeamId: string
) {
  return currentTeamId === clusterObject?.owningTeamId
    ? []
    : clusterObject?.availableNamespaces ?? []
}
